import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useT } from '../i18n';

const BRAND = 'Lorath';

// Pfad-Muster → i18n-Schluessel, zentral statt in ~25 Seiten verstreut. ALLE
// Schluessel stammen aus dem Server-i18n (server/i18n/ui/de.json → "keys") und
// existieren damit in allen 68 Sprachen — ein hier erfundener Schluessel stuende
// als roher Key im Browser-Tab. Bewusst NUR neutrale Labels ohne Platzhalter und
// ohne eigenes „Lorath"-Praefix (die page.*.title-Familie tragt es schon).
// Dynamische Segmente werden als Muster gematcht, nicht als exakte Strings;
// spezifischere Muster stehen zuerst.
const ROUTE_TITLE_KEYS: readonly (readonly [RegExp, string])[] = [
  [/^\/login$/, 'auth.tab_login'],
  [/^\/verify-email$/, 'verify_email.title'],
  [/^\/legal(\/|$)/, 'pwa.legal_title'],
  [/^\/kontakt$/, 'contact.title'],
  [/^\/gruender$/, 'creator.page_title'],
  [/^\/onboarding$/, 'onboarding.page_title'],
  [/^(\/|\/dashboard)$/, 'dashboard.title'],
  [/^\/campaign-new$/, 'wizard.title'],
  // W2-B: Sofortstart an den Tueren und die „Mehr"-Uebersicht.
  [/^\/first-scene\/adventure$/, 'first_scene.adventure.title'],
  [/^\/first-scene\/companion$/, 'first_scene.companion.title'],
  [/^\/more$/, 'nav.more'],
  // W1-C: die beiden neuen Tab-Flaechen (Platzhalter bis W4-B/W4-C).
  [/^\/chats$/, 'nav.chats'],
  [/^\/discover$/, 'nav.discover'],
  [/^\/characters\/new$/, 'character_edit.title_new'],
  [/^\/characters\/[^/]+\/edit$/, 'character_edit.title_edit'],
  // R2-07: /characters selbst leitet auf /chats um; die Verwaltung liegt unter
  // /characters/manage. Der Alt-Eintrag bleibt fuer den Augenblick vor dem
  // Redirect stehen.
  [/^\/characters\/manage$/, 'chars.title'],
  [/^\/characters$/, 'chars.title'],
  [/^\/world$/, 'world.overview_title'],
  [/^\/world-element-edit$/, 'nav.world_building_link'],
  [/^\/world-tree$/, 'nav.world_tree'],
  [/^\/spielbau$/, 'nav.group_gamebuild'],
  [/^\/table-rounds\/new$/, 'tr_new.title'],
  [/^\/table-rounds$/, 'tr.title'],
  [/^\/soundboard$/, 'soundboard.title'],
  [/^\/coins$/, 'coins.title'],
  [/^\/settings\/notifications$/, 'settings.notifications.section_title'],
  [/^\/settings$/, 'settings.title'],
  [/^\/admin$/, 'nav.admin'],
  [/^\/profile$/, 'profile.title'],
  [/^\/tindra$/, 'pwa.tindra'],
  [/^\/milestones$/, 'milestones.title'],
  [/^\/intimate-videos$/, 'intimate_videos.page_title'],
  [/^\/chat\/[^/]+$/, 'nav.campaigns'],
  [/^\/character-chat\/[^/]+$/, 'chars.chat_btn'],
  [/^\/group-chat\/[^/]+$/, 'group_chat.title'],
  [/^\/table-round-chat\/[^/]+$/, 'nav.table_rounds'],
  [/^\/table-round-session\/[^/]+$/, 'tr_session.title'],
];

function titleKeyFor(pathname: string): string | null {
  // Trailing Slash normalisieren (/coins/ === /coins), Root bleibt '/'.
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  for (const [pattern, key] of ROUTE_TITLE_KEYS) {
    if (pattern.test(path)) return key;
  }
  return null;
}

// Setzt den Browser-Tab-Titel fuer JEDE Route an einer Stelle. Vorher taten das
// nur Chat und Tafelrunden-Chat — beide ohne Cleanup, so dass der Chat-Titel
// nach dem Zuruecknavigieren im Tab kleben blieb. Auf dem Desktop sind Tabs,
// History und Lesezeichen sonst nicht unterscheidbar ("Lorath" ueberall).
export function DocumentTitle() {
  const { t } = useT();
  const { pathname } = useLocation();

  useEffect(() => {
    const key = titleKeyFor(pathname);
    const label = key ? t(key) : '';
    // Solange die Uebersetzungs-Map noch nicht geladen ist, gibt t() den rohen
    // Schluessel zurueck — der darf NIE im Tab landen, dann lieber nur die Marke.
    if (!label || label === key) {
      document.title = BRAND;
      return;
    }
    // Labels, die die Marke schon tragen ("Willkommen in Lorath"), nicht doppeln.
    document.title = label.includes(BRAND) ? label : `${label} – ${BRAND}`;
  }, [pathname, t]);

  return null;
}
